import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ModuleContext from '../../_contexts/ModuleContext';
import { getMedialinks, Medialink } from '../../../../api/medialink';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  getSharedUserMaterial,
  getUserMaterial,
  SharedUserMaterial,
  UserMaterial,
} from '../../../../api/user-material';
import {
  getSharedSlideSets,
  getSlideSetsBySearch,
  SharedSlideSet,
  SlideSet,
  SlideSetOwner,
} from '../../../../api/slide-set';
import { flattenCategoricalMaterialPerModule, virtualContent } from '../utils/categoricalMaterialUtils';
import { CategoricalContentTypeContext } from './CategoricalContentTypeContext';
import { flattenMaterial, orderByReference } from '../utils/queryMaterialUtils';
import useGetMaterialType, { MATERIAL_TYPE } from '../hooks/getMaterialType';
import orderBy from 'lodash/orderBy';
import { NodeHierarchy } from '../../api/materialApi';
import { useFeatureToggles } from '../../../../contexts/FeatureTogglesContext';

export const DEFAULT_MATERIAL_RESULTS = {
  medialinks: {
    data: [] as Medialink[],
    total: 0,
  },
  userMaterial: {
    data: [] as UserMaterial[],
    total: 0,
  },
  slideSets: {
    data: [] as SlideSet[],
    total: 0,
  },
  sharedMaterial: {
    data: [] as SharedMaterial[],
    total: 0,
  },
};

export type SharedMaterial = (SharedUserMaterial | SharedSlideSet) & { hierarchy: NodeHierarchy[] };

export type MaterialResults = {
  medialinks: {
    data: Medialink[];
    total: number;
  };
  userMaterial: {
    data: UserMaterial[];
    total: number;
  };
  slideSets: {
    data: SlideSet[];
    total: number;
  };
  sharedMaterial: {
    data: SharedMaterial[];
    total: number;
  };
};

export const SEARCH_PARAMS = {
  SEARCH: 'search',
  SELECTED_MODULE_IDS: 'modules',
  CONTENT_TYPE_IDS: 'contentTypes',
};

type SearchParamValue = typeof SEARCH_PARAMS[keyof typeof SEARCH_PARAMS];

export const QueryMaterialContext = createContext<{
  loading: boolean;
  materialResults: MaterialResults;
  modulesForResults: string[];
  refetch: () => void;
  setSearchParam: (params: Partial<Record<SearchParamValue, string>>) => void;
}>({
  loading: false,
  materialResults: DEFAULT_MATERIAL_RESULTS,
  modulesForResults: [],
  refetch: () => {},
  setSearchParam: () => {},
});

export const QueryMaterialContextProvider = ({ children }: { children: ReactNode }) => {
  const { materialCategory } = useParams();
  const { module, allModules: modules } = useContext(ModuleContext);
  const { categoricalContentPerModule, loading } = useContext(CategoricalContentTypeContext);
  const featureToggles = useFeatureToggles();
  const [isLoading, setIsLoading] = useState(false);
  const [materialResults, setMaterialResults] = useState<MaterialResults>(DEFAULT_MATERIAL_RESULTS);
  const [searchParams, setSearchParams] = useSearchParams();

  const categoricalContent = useMemo(
    () => flattenCategoricalMaterialPerModule(categoricalContentPerModule),
    [categoricalContentPerModule],
  );

  const search = searchParams.get(SEARCH_PARAMS.SEARCH) || undefined;
  const selectedModuleIds = useMemo(
    () => searchParams.get(SEARCH_PARAMS.SELECTED_MODULE_IDS)?.split(',') || [],
    [searchParams],
  );

  const materialType = useGetMaterialType(materialCategory);
  const contentTypeIds =
    searchParams.get(SEARCH_PARAMS.CONTENT_TYPE_IDS) ??
    categoricalContent
      .find(c => c.name === materialCategory)
      ?.contentTypes.map(x => x.id)
      .join(',');

  useEffect(() => {
    setIsLoading(false);
    setMaterialResults(DEFAULT_MATERIAL_RESULTS);
  }, [materialCategory, setSearchParams]);

  const searchMaterial = useCallback(async () => {
    if (!module || !materialCategory || loading) return;

    setIsLoading(true);

    const material = { ...DEFAULT_MATERIAL_RESULTS };

    const [noModulesSelected, allModulesSelected] = [
      selectedModuleIds.length === 0,
      selectedModuleIds.length === modules.length,
    ];
    const shouldFetchAllModules = noModulesSelected || allModulesSelected;

    const queryBaseParams = {
      search,
      includeSubModules: shouldFetchAllModules,
    };
    const filterByModuleIds = shouldFetchAllModules ? [module.id] : selectedModuleIds;

    switch (materialType) {
      case MATERIAL_TYPE.MEDIALINK:
        {
          const queryParams = { ...queryBaseParams, contentTypeIds, includeScore: true };
          const allMedialinks = await Promise.all(
            filterByModuleIds.map(moduleId => getMedialinks(moduleId, queryParams)),
          );
          material.medialinks = flattenMaterial(allMedialinks);
        }
        break;
      case MATERIAL_TYPE.USER_MATERIAL:
        {
          const showUserMaterial = contentTypeIds?.includes(virtualContent.teacher.userMaterial) ?? true;

          if (showUserMaterial) {
            const queryParams = {
              ...queryBaseParams,
              includeScore: true,
              includeNodeHierarchy: true,
              ...(featureToggles.BUNDLES ? { includeBundles: true } : {}),
            };
            const allUserMaterial = await Promise.all(
              filterByModuleIds.map(moduleId => getUserMaterial(moduleId, queryParams)),
            );
            const userMaterial = flattenMaterial(allUserMaterial);
            material.userMaterial = {
              data: orderBy(userMaterial.data, ['lastUpdatedAt'], ['desc']),
              total: userMaterial.total,
            };
          }
        }
        break;
      case MATERIAL_TYPE.SLIDE_SET:
        {
          const [showPelckmans, showUser, showStudent] = [
            contentTypeIds?.includes(virtualContent.teacher.slideSets),
            contentTypeIds?.includes(virtualContent.teacher.userSlideSets),
            contentTypeIds?.includes(virtualContent.student.slideSets),
          ];

          if (!showPelckmans && !showUser && !showStudent) break;

          const filterOwnerType: SlideSetOwner | undefined =
            showPelckmans === showUser ? undefined : showPelckmans ? 'pelckmans' : 'user';
          const queryParams = { ...queryBaseParams, owner: filterOwnerType, includeNodeHierarchy: true };

          const allSlideSets = await Promise.all(
            filterByModuleIds.map(moduleId => getSlideSetsBySearch(moduleId, queryParams)),
          );
          material.slideSets = flattenMaterial(allSlideSets);
        }
        break;
      case MATERIAL_TYPE.SHARED_MATERIAL:
        {
          const [showUserMaterial, showSlideSets] = [
            contentTypeIds?.includes(virtualContent.student.sharedMaterial),
            contentTypeIds?.includes(virtualContent.student.sharedSlideSets),
          ];
          const showBoth = showUserMaterial === showSlideSets;
          const shouldFetchUserMaterial = showBoth || showUserMaterial;
          const shouldFetchSlideSets = showBoth || showSlideSets;

          const sharedUserMaterial = shouldFetchUserMaterial
            ? flattenMaterial(
                await Promise.all(
                  filterByModuleIds.map(async moduleId =>
                    getSharedUserMaterial(moduleId, {
                      ...queryBaseParams,
                      includeScore: true,
                      includeNodeHierarchy: true,
                    }),
                  ),
                ),
              )
            : DEFAULT_MATERIAL_RESULTS.sharedMaterial;

          const sharedSlideSets = shouldFetchSlideSets
            ? flattenMaterial(
                await Promise.all(
                  filterByModuleIds.map(async moduleId =>
                    getSharedSlideSets(moduleId, { ...queryBaseParams, includeNodeHierarchy: true }),
                  ),
                ),
              )
            : DEFAULT_MATERIAL_RESULTS.sharedMaterial;

          material.sharedMaterial = {
            data: [
              ...orderBy(sharedUserMaterial.data as SharedMaterial[], ['lastUpdatedAt'], ['asc']),
              ...(sharedSlideSets.data as SharedMaterial[]),
            ],
            total: (sharedUserMaterial.total || 0) + (sharedSlideSets.total || 0),
          };
        }
        break;
    }

    setMaterialResults(material);
    setIsLoading(false);
  }, [
    contentTypeIds,
    featureToggles.BUNDLES,
    loading,
    materialCategory,
    materialType,
    module,
    modules.length,
    search,
    selectedModuleIds,
  ]);

  useEffect(() => {
    searchMaterial();
  }, [searchMaterial]);

  const modulesForResults = useMemo(() => {
    const moduleIds = modules.map(x => x.id);

    if (selectedModuleIds.length === 0 && module) {
      return moduleIds;
    } else {
      return orderByReference(moduleIds, selectedModuleIds);
    }
  }, [module, modules, selectedModuleIds]);

  const refetch = useCallback(() => {
    searchMaterial();
  }, [searchMaterial]);

  const setSearchParam = (params: Partial<Record<SearchParamValue, string>>) => {
    const newSearchParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === '') {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, value);
      }
    });
    setSearchParams(newSearchParams);
  };

  return (
    <QueryMaterialContext.Provider
      value={{ loading: isLoading, modulesForResults, materialResults, refetch, setSearchParam }}
    >
      {children}
    </QueryMaterialContext.Provider>
  );
};
